import { CATEGORY_TYPES, ROUTES } from 'shared/constants';
import { isSection } from 'shared/utils/category.utils';

/**
 * Build a relative knowledge URL (path + search + hash) for a category.
 *
 * Section vs folder is determined by parent-path depth-parity (via `isSection`):
 *   - Section (even parent-path length, type='category'):
 *       /knowledge[/:parentDir]?workspace=X#:categoryId
 *   - Folder (odd parent-path length, type='category'):
 *       /knowledge/:categoryId?workspace=X
 *   - Binder (type='knowledge'):
 *       /knowledge[/:parentDir]?workspace=X&category=:categoryId
 *
 * @param {{ fullPath: string, workspaceId: string, type: string }} params
 *   - `fullPath` is the category's full path including the category itself
 *     (parent chain + self id, comma-separated, prefixed with 'root').
 * @returns {string} relative URL (path + ?search + #hash)
 */
export function buildKnowledgeLink({ fullPath, workspaceId, type }) {
  if (!fullPath || !workspaceId) return ROUTES.KNOWLEDGE;

  const parts = fullPath.split(',').filter(Boolean);
  const withoutRoot = parts.slice(1);
  const categoryId = withoutRoot[withoutRoot.length - 1];
  const workspaceParam = `workspace=${workspaceId}`;

  if (withoutRoot.length === 0) {
    return `${ROUTES.KNOWLEDGE}?${workspaceParam}`;
  }

  // Self's parent chain. Even length → section (per `isSection`).
  const isCategorySection = isSection(parts.slice(0, -1));

  // Deepest non-section ancestor — the "folder" we anchor the URL on (its own
  // route is /knowledge/:id). Parity of each ancestor's parent-path picks section vs folder.
  const ancestors = withoutRoot.slice(1, -1);
  const parentDir =
    ancestors.findLast((_, i) => !isSection(parts.slice(0, i + 2))) ?? null;

  if (isCategorySection) {
    const pathname = parentDir
      ? ROUTES.CONTENT_ITEM.replace(':id', parentDir)
      : ROUTES.KNOWLEDGE;
    return `${pathname}?${workspaceParam}#${categoryId}`;
  }

  if (type === CATEGORY_TYPES.CATEGORY) {
    return `${ROUTES.CONTENT_ITEM.replace(':id', categoryId)}?${workspaceParam}`;
  }

  const pathname = parentDir
    ? ROUTES.CONTENT_ITEM.replace(':id', parentDir)
    : ROUTES.KNOWLEDGE;
  return `${pathname}?${workspaceParam}&category=${categoryId}`;
}
